import React from 'react';
import { Link } from 'react-router-dom';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { useTranslation } from '../hooks/useTranslation';

const Shares = () => {
  const { handleButtonClick } = useButtonLinks();
  const { t } = useTranslation();
  const sharesInstruments = [
    { symbol: 'AAPL', description: 'Apple Inc.', contractSize: '1 USD', marginRate: '0.1' },
    { symbol: 'AMZN', description: 'Amazon.com Inc', contractSize: '1 USD', marginRate: '0.2' },
    { symbol: 'BA', description: 'The Boeing Company', contractSize: '1 USD', marginRate: '0.15' },
    { symbol: 'BABA', description: 'Alibaba Group Holding Ltd', contractSize: '1 USD', marginRate: '0.2' },
    { symbol: 'CSCO', description: 'Cisco Systems', contractSize: '1 USD', marginRate: '0.1' },
    { symbol: 'DIS', description: 'Walt Disney Co', contractSize: '1 USD', marginRate: '0.15' },
    { symbol: 'EBAY', description: 'EBAY Inc', contractSize: '1 USD', marginRate: '0.1' },
    { symbol: 'MSFT', description: 'Microsoft Corporation', contractSize: '1 USD', marginRate: '0.15' },
    { symbol: 'NFLX', description: 'Netflix', contractSize: '1 USD', marginRate: '0.15' },
    { symbol: 'NKE', description: 'NIKE Inc', contractSize: '1 USD', marginRate: '0.15' },
    { symbol: 'PYPL', description: 'PayPal Holdings', contractSize: '1 USD', marginRate: '0.2' },
    { symbol: 'TSLA', description: 'Tesla Motor', contractSize: '1 USD', marginRate: '0.15' }
  ];

  return (
    <div className="shares-page">
      {/* Hero Section */}
      <section className="shares-hero">
        <div className="container">
          <div className="shares-hero-content">
            <h1 className="shares-title">{t('markets.shares.hero.title')}</h1>
            <p className="shares-subtitle">
              {t('markets.shares.hero.subtitle')}
            </p>
            <div className="hero-question">
              <h2>{t('markets.shares.hero.question')}</h2>
            </div>
            <div className="shares-features">
              <div className="feature-highlight">
                <i className="fas fa-award"></i>
                <span>{t('markets.shares.hero.platform')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-smile"></i>
                <span>{t('markets.shares.hero.clients')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-tools"></i>
                <span>{t('markets.shares.hero.tools')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shares Instruments Section */}
      <section className="shares-instruments">
        <div className="container">
          <div className="instruments-header">
            <h2 className="section-title">{t('markets.shares.instruments.title')}</h2>
            <button className="btn btn-secondary">{t('markets.shares.instruments.button')}</button>
          </div>
          
          <div className="instruments-table-wrapper">
            <table className="instruments-table">
              <thead>
                <tr>
                  <th>{t('markets.shares.instruments.headers.symbol')}</th>
                  <th>{t('markets.shares.instruments.headers.description')}</th>
                  <th>{t('markets.shares.instruments.headers.contractSize')}</th>
                  <th>{t('markets.shares.instruments.headers.marginRate')}</th>
                </tr>
              </thead>
              <tbody>
                {sharesInstruments.map((instrument, index) => (
                  <tr key={index}>
                    <td className="symbol">{instrument.symbol}</td>
                    <td className="description">{instrument.description}</td>
                    <td>{instrument.contractSize}</td>
                    <td className="margin-rate">{instrument.marginRate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <p className="instruments-note">
            {t('markets.shares.instruments.note')}
          </p>
        </div>
      </section>

      {/* Why Trade Shares Section */}
      <section className="why-trade-shares">
        <div className="container">
          <h2 className="section-title">{t('markets.shares.whyTrade.title')}</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <h3>{t('markets.shares.whyTrade.benefits.0.title')}</h3>
              <p>{t('markets.shares.whyTrade.benefits.0.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-university"></i>
              </div>
              <h3>{t('markets.shares.whyTrade.benefits.1.title')}</h3>
              <p>{t('markets.shares.whyTrade.benefits.1.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-bolt"></i>
              </div>
              <h3>{t('markets.shares.whyTrade.benefits.2.title')}</h3>
              <p>{t('markets.shares.whyTrade.benefits.2.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-dollar-sign"></i>
              </div>
              <h3>{t('markets.shares.whyTrade.benefits.3.title')}</h3>
              <p>{t('markets.shares.whyTrade.benefits.3.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-bullseye"></i>
              </div>
              <h3>{t('markets.shares.whyTrade.benefits.4.title')}</h3>
              <p>{t('markets.shares.whyTrade.benefits.4.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h3>{t('markets.shares.whyTrade.benefits.5.title')}</h3>
              <p>{t('markets.shares.whyTrade.benefits.5.description')}</p>
            </div>
          </div>
          
          <div className="shares-cta">
            <h3>{t('markets.shares.hero.subtitle')}</h3>
            <button 
              className="btn btn-primary"
              onClick={(e) => handleButtonClick('openAccount', e)}
            >
              {t('nav.openAccount')}
            </button>
          </div>
        </div>
      </section>

      {/* Account Comparison Section */}
      <section className="account-comparison">
        <div className="container">
          <div className="comparison-header">
            <h2 className="section-title">{t('markets.shares.accountComparison.title')}</h2>
          </div>
          
          <div className="accounts-cards">
            
            {/* Pro Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">{t('markets.shares.accountComparison.accounts.pro')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$20</span>
                  <span className="deposit-label">{t('markets.shares.accountComparison.features.startingDeposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.spreadsFrom')}</span>
                  <span className="feature-value">1 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.commission')}</span>
                  <span className="feature-value">$0</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.stopOutLevel')}</span>
                  <span className="feature-value">20%</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.islamicAccounts')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.orderExecution')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.market')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.minOrderSize')}</span>
                  <span className="feature-value">0.01</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.accountCurrency')}</span>
                  <span className="feature-value">USD</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.hedging')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.yes')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.ea')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.allowed')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.tradingInstruments')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.fxMetalsCommodities')}</span>
                </div>
              </div>
              <Link to="/accounts/pro" className="btn btn-primary account-btn">{t('markets.shares.accountComparison.features.findOutMore')}</Link>
            </div>

            {/* Prime Account */}
            <div className="account-card featured">
              <div className="popular-badge">{t('markets.shares.accountComparison.mostPopular')}</div>
              <div className="account-header">
                <h3 className="account-name">{t('markets.shares.accountComparison.accounts.prime')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$250</span>
                  <span className="deposit-label">{t('markets.shares.accountComparison.features.startingDeposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.spreadsFrom')}</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.commission')}</span>
                  <span className="feature-value">$6</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.stopOutLevel')}</span>
                  <span className="feature-value">20%</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.islamicAccounts')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.orderExecution')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.market')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.minOrderSize')}</span>
                  <span className="feature-value">0.01</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.accountCurrency')}</span>
                  <span className="feature-value">USD</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.hedging')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.yes')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.ea')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.allowed')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.tradingInstruments')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.fxMetalsCommodities')}</span>
                </div>
              </div>
              <Link to="/accounts/prime" className="btn btn-primary account-btn">{t('markets.shares.accountComparison.features.findOutMore')}</Link>
            </div>

            {/* Premium Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">{t('markets.shares.accountComparison.accounts.premium')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$3000</span>
                  <span className="deposit-label">{t('markets.shares.accountComparison.features.startingDeposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.spreadsFrom')}</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.commission')}</span>
                  <span className="feature-value">$3</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.stopOutLevel')}</span>
                  <span className="feature-value">20%</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.islamicAccounts')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.orderExecution')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.market')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.minOrderSize')}</span>
                  <span className="feature-value">0.01</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.accountCurrency')}</span>
                  <span className="feature-value">USD</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.hedging')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.yes')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.ea')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.allowed')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('markets.shares.accountComparison.features.tradingInstruments')}</span>
                  <span className="feature-value">{t('markets.shares.accountComparison.features.fxMetalsCommodities')}</span>
                </div>
              </div>
              <Link to="/accounts/premium" className="btn btn-primary account-btn">{t('markets.shares.accountComparison.features.findOutMore')}</Link>
            </div>

          </div>
          
          <div className="demo-account">
            <h3>{t('markets.shares.accountComparison.demo.title')}</h3>
            <p>{t('markets.shares.accountComparison.demo.description')}</p>
            <button 
              className="btn btn-secondary"
              onClick={(e) => handleButtonClick('tryDemo', e)}
            >
              {t('markets.shares.accountComparison.demo.button')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shares;